import { useState } from "react";
import EntityList from "Views/Components/EntityList/EntityList";
import TablePagination from "Views/Components/Pagination/TablePagination";
import { BoardDto } from "./DirectorSectionTypes";
import axios from "axios";
import { SERVER_URL } from "Constants";
import alertToast from "Util/ToastifyUtils";
import useAsync from "Hooks/useAsync";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { extractDateFromDateInt } from "./DirectorInformationSection";
import { validateAbnFormatWrapper, validateAcnFormat } from "Util/BusinessNumberValidationFormat";
import { DirectorBoardSearchModal, ExtendedBoardDto } from "./DirectorBoardSearch/DirectorBoardSearchModal";
import { PaginatedResponse } from "Types/PaginatedResponse";

const defaultResponse: PaginatedResponse<BoardDto> = {
	data: [],
	pageNo: 1,
	pageSize: 3,
	totalCount: 0,
	totalPages: 0
}

const Default_PAGE_SIZE = 3;

const fetchDirectorBoardDetails = async (directorId: string, isCurrent: boolean, pageNo: number = 1) => {
	try {
		const response = await axios.get<PaginatedResponse<BoardDto>>(`${SERVER_URL}/api/directors/${directorId}/boards`, {
			params: {
				pageNo,
				pageSize: Default_PAGE_SIZE,
				currentBoardsOnly: isCurrent
			}
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const axiosError = error as any;
			alertToast(axiosError.response?.data?.message || axiosError.message, "error");
			console.error(axiosError);
			return defaultResponse;
		}

		alertToast("Error fetching director details", "error");
		console.error(error);
		return defaultResponse;
	}
}

export const DirectorBoardDetailSection = ({ directorId, isCurrent }: { directorId: string, isCurrent: boolean }) => {
	const preText = isCurrent ? "Current" : "Previous";
	const [pageNo, setPageNo] = useState(0);
	const [selectedBoardDetail, setSelectedBoardDetail] = useState<ExtendedBoardDto | undefined>(undefined); 

	const response = useAsync(async () => {
		const result = await fetchDirectorBoardDetails(directorId, isCurrent, pageNo + 1);
		return result
	}, [directorId, isCurrent, pageNo]);

	const renderNumber = (
		number: string | undefined,
		validator: (input: string) => { isValid: boolean; value?: string },
		defaultValue: string = "-"
	) => {
		if (!number) {
			return defaultValue;
		}
	
		const { isValid, value } = validator(number);
		if (!isValid) {
			return number;
		}
	
		return <span className="link-item">{value || number}</span>;
	};
	
	const renderBusinessNumber = (board: BoardDto) => {
		return renderNumber(board.businessNumber, validateAbnFormatWrapper);
	};
	
	const renderCompanyNumber = (board: BoardDto) => {
		return renderNumber(board.companyNumber, input => validateAcnFormat(input, true));
	};

	const handleClick = (
		board: BoardDto,
		number: string | undefined,
		validator: (input: string) => { isValid: boolean; value?: string },
		businessNumberType: "ACN" | "ABN"
	) => {
		if (!number) {
			return;
		}
	
		const { isValid, value } = validator(number);
		if (!isValid) {
			return;
		}

		setSelectedBoardDetail({
			...board,
			searchType: businessNumberType,
			searchTerm: value || number
		});
	}
	
	const handleBusinessNumberClick = (board: BoardDto) => {
		return handleClick(board, board.businessNumber, input => validateAbnFormatWrapper(input), "ABN");
	}

	const handleCompanyNumberClick = (board: BoardDto) => {
		return handleClick(board, board.companyNumber, input => validateAcnFormat(input, true), "ACN");
	}

	if (response.type === "loading") {
		return <InlineSpinner />;
	}

	if (!!!response.data) {
		return <div className="alert alert__warning">No directorships found</div>;
	}

	const boardDetails = response.data;

	return (
		<>
			<div className="director-board-table">
				<h6>{preText} directorships on other boards:</h6>
				{boardDetails.data.length > 0 && (
					<EntityList
						collection={boardDetails.data}
						columns={[
							{
								displayName: "Company Name",
								columnName: "organisationName",
								value: (board: BoardDto) => board.organisationName
							},
							{
								displayName: "Company Number",
								columnName: "companyNumber",
								value: (board: BoardDto) => renderCompanyNumber(board),
								onClickValue: (board: BoardDto) => handleCompanyNumberClick(board),
								className: "link"
							},
							{
								displayName: "Business Number",
								columnName: "businessNumber",
								value: (board: BoardDto) => renderBusinessNumber(board),
								onClickValue: (board: BoardDto) => handleBusinessNumberClick(board),
								className: "link",
							},
							{
								displayName: "Company Status",
								columnName: "companyStatus",
								value: (board: BoardDto) => board.companyStatus,
								hide: !isCurrent
							},
							{
								displayName: "Date Deregistered",
								columnName: "deregisteredDate",
								value: (board: BoardDto) => extractDateFromDateInt(board.deregisteredDate),
								hide: isCurrent
							},
							{
								displayName: "Reason Deregistered",
								columnName: "reasonDeregistered",
								value: (board: BoardDto) => board.reasonDeregistered || "-",
								hide: isCurrent
							}
						]}
						idColumn="id"
						sortColumn=""
						sortDescending
					/>
				)}
				{boardDetails.data.length === 0 && <div className="alert alert__warning">No directorships found. Please try again later.</div>}
				{boardDetails.totalCount > Default_PAGE_SIZE && (
					<TablePagination
						pageNo={pageNo}
						totalRecords={boardDetails.totalCount}
						perPage={Default_PAGE_SIZE}
						onPageChange={setPageNo}
					/>
				)}
			</div>
			{selectedBoardDetail && (<DirectorBoardSearchModal boardDetail={selectedBoardDetail} isCurrent={isCurrent} onClose={() => {
				setSelectedBoardDetail(undefined);
				sessionStorage.removeItem("generateCreditReportState");
			}} />)}
		</>
	)
}